/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */

import {
  checkContactPermission,
  openContactPicker,
  openSystemSettings,
} from '@/api/native/contactImport';
import { ContactPermissionStatusEnum } from '@/api/native/types';
import { Dialog } from '@hb/coffee';

import { TP_CONTACT_IMPORT_ICON, TP_CONTACT_IMPORT_POPUP, TP_CONTACT_IMPORT_POPUP_CONFIRM } from '@/views_v2/verify-credit/tianqi';
import type { ContactInfo } from './types';
import { ubtSetupModuleExpose, ubtSetupPageButton } from '../ubt/ubt-setup';
import platform from '../platform';
import { ubtSendError } from '../ubt';

/**
 * 显示权限引导弹窗（已拒绝过）
 * @returns 用户是否确认去设置
 * 弹窗的zIndex需要比PopupView的zIndex高，否则会被PopupView遮挡
 */
const showPermissionGuideDialog = (): Promise<boolean> => new Promise((resolve) => {
  ubtSetupModuleExpose(TP_CONTACT_IMPORT_POPUP);

  Dialog.confirm({
    title: '快速添加联系人',
    message: '开启通讯录，快速添加联系人信息，臻有钱仅使用通讯录添加联系人信息，不会保存你的通讯录',
    confirmButtonText: '去授权',
    cancelButtonText: '下次再说',
    zIndex: 2010,
  })
    .then(() => {
      ubtSetupPageButton(TP_CONTACT_IMPORT_POPUP_CONFIRM);
      resolve(true);
    })
    .catch(() => resolve(false));
});

/**
 * 处理权限引导弹窗和系统设置跳转，状态===3
 */
const handlePermissionRejected = async (): Promise<void> => {
  const userConfirmed = await showPermissionGuideDialog();
  if (userConfirmed) {
    await openSystemSettings();
  }
};

/**
 * 处理联系人选择结果
 * @param result selectContact 返回的结果
 * @returns 联系人信息或 null
 */
const handleContactPickerResult = (
  result: Awaited<ReturnType<typeof openContactPicker>>,
): ContactInfo | null => {
  if (result.status) {
    // 数据管控状态（5）：已经在 openContactPicker 中显示 Toast，直接返回，不弹授权弹窗
    if (result.status === ContactPermissionStatusEnum.DATA_CONTROL) {
      return null;
    }

    // 已拒绝状态（3）：显示授权引导弹窗
    if (result.status === ContactPermissionStatusEnum.REJECTED) {
      // Android状态再次上报
      if (platform.isAndroid) {
        ubtSetupPageButton(TP_CONTACT_IMPORT_ICON, {
          authType: result.status,
        });
      }
      handlePermissionRejected();
      return null;
    }
  }

  if (result.contactInfo) {
    return result.contactInfo as ContactInfo;
  }
  return null;
};

/**
 * 公共的初始化逻辑（权限检查、埋点）
 */
const initImportFlow = async (): Promise<number> => {
  const permissionStatus = await checkContactPermission();

  ubtSetupPageButton(TP_CONTACT_IMPORT_ICON, {
    authType: permissionStatus,
  });

  return Number(permissionStatus);
};

/**
 * 处理联系人导入点击
 *
 * 流程说明：
 * - Android: getContactsPermissionStatus 只返回 0, 1, 2（不返回 3）
 *   当收到 1 或 2 时，直接调用 selectContact，如果是真正的永久拒绝（状态3），会返回状态3
 * - iOS: getContactsPermissionStatus 可以返回 0, 1, 2, 3, 4
 *   如果状态是 3，直接处理权限拒绝；否则调用 selectContact

 */
export const handleImportClick = async (): Promise<ContactInfo | null> => {
  try {
    const permissionStatus = await initImportFlow();

    if (typeof permissionStatus !== 'number') {
      return null;
    }

    // 根据权限状态执行不同流程
    switch (permissionStatus) {
      case ContactPermissionStatusEnum.AUTHORIZED:
      case ContactPermissionStatusEnum.UNDECIDED:
      case ContactPermissionStatusEnum.RESTRICTED: {
        // 直接打开通讯录选择页
        const result = await openContactPicker();
        return handleContactPickerResult(result);
      }

      case ContactPermissionStatusEnum.REJECTED: {
        // 已拒绝过（状态3）：
        if (platform.isIOS) {
          await handlePermissionRejected();
        }
        return null;
      }

      case ContactPermissionStatusEnum.UNKNOWN:
        return null;
      default:
        return null;
    }
  } catch (error) {
    ubtSendError(error as Error);
    return null;
  }
};
